import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useActions } from "../context/ActionsContext";
import { PageHeader, StatCard, TableWrap, Th, Td, Loading, EmptyState } from "../components/shared";
import { formatINR, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { AttachmentViewer, AttachmentThumb } from "../components/AttachmentViewer";
import { AgeBadge } from "./Payees";
import { ArrowLeft, ArrowUpRight, Wallet, Scale, Printer, Paperclip } from "lucide-react";

function PurposeCard({ title, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
      <h3 className="font-heading font-semibold text-sm mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {items.map((p) => (
          <div key={p.name} className="border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2">
            <div className="text-[11px] text-slate-500">{p.name}</div>
            <div className={`font-mono font-semibold ${color}`}>{formatINR(p.amount)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Buckets({ b, color }) {
  return (
    <div className="grid grid-cols-4 gap-1.5 mb-3">
      {Object.entries(b).map(([k, v]) => (
        <div key={k} className="rounded-lg bg-slate-50 dark:bg-slate-800/50 px-2 py-1.5 text-center">
          <div className="text-[10px] text-slate-400">{k} days</div>
          <div className={`font-mono text-xs font-semibold ${v > 0 ? color : "text-slate-300"}`}>{formatINR(v)}</div>
        </div>
      ))}
    </div>
  );
}

function AgingCard({ title, rows, buckets, color, testid, emptyText, onAction, actionLabel }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-sm">{title}</h3>
        {rows.length > 0 && onAction && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAction} data-testid={`${testid}-action`}>{actionLabel}</Button>}
      </div>
      <Buckets b={buckets} color={color} />
      {rows.length === 0 ? <p className="text-xs text-slate-400">{emptyText}</p> : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((r) => (
            <div key={r.id} className="py-2 flex items-center gap-3 text-xs" data-testid={`${testid}-row`}>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-slate-700 dark:text-slate-200">{r.txn_number}{r.bill_number ? ` · Bill ${r.bill_number}` : ""}</div>
                <div className="text-[11px] text-slate-400 truncate">{formatDate(r.txn_date)}{r.description ? ` · ${r.description}` : ""}</div>
              </div>
              <div className="text-right">
                <div className={`font-mono font-semibold ${color}`}>{formatINR(r.open)}</div>
                {r.open !== r.amount && <div className="text-[10px] text-slate-400">of {formatINR(r.amount)}</div>}
              </div>
              <AgeBadge days={r.age_days} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PayeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open } = useActions();
  const [payee, setPayee] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [aging, setAging] = useState(null);
  const [viewIds, setViewIds] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l, a] = await Promise.all([api.get(`/payees/${id}`), api.get(`/payees/${id}/ledger`), api.get(`/payees/${id}/aging`)]);
      setPayee(p.data); setLedger(l.data); setAging(a.data);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  if (loading || !payee) return <Loading />;
  const s = payee.summary;
  const scrollToLedger = () => document.getElementById("pd-ledger")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div>
      <button onClick={() => navigate("/payees")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-4 w-4" /> Back to Payees</button>
      <PageHeader title={payee.name} subtitle={`${payee.type} · ${payee.mobile || ""} ${payee.company_name || ""}`}
        actions={<div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="pd-print" className="gap-1.5"><Printer className="h-3.5 w-3.5" /> Statement</Button>
          <Button size="sm" variant="outline" onClick={() => open("give_advance", { payee_id: id })} data-testid="pd-advance" className="gap-1.5"><ArrowUpRight className="h-3.5 w-3.5" /> Give Advance</Button>
          <Button size="sm" variant="outline" onClick={() => open("adjust_advance", { payee_id: id })} data-testid="pd-adjust" className="gap-1.5"><Scale className="h-3.5 w-3.5" /> Adjust</Button>
          <Button size="sm" variant="outline" onClick={() => open("pay_agency", { payee_id: id })} data-testid="pd-expense" className="gap-1.5"><Wallet className="h-3.5 w-3.5" /> Pay / Bill</Button>
        </div>} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div onClick={scrollToLedger} className="cursor-pointer"><StatCard label="Total Expense" value={s.total_expense} accent="amber" sub="Recognized bills + expenses" isCurrency={true} testid="pd-total-expense" /></div>
        <div onClick={scrollToLedger} className="cursor-pointer"><StatCard label="Total Paid" value={s.total_paid} accent="rose" sub={`${s.payments_count} payment(s)`} isCurrency={true} testid="pd-total-paid" /></div>
        <div onClick={scrollToLedger} className="cursor-pointer"><StatCard label="Advance Pending" value={s.advance_pending} accent="violet" sub="Paid, not yet allocated" isCurrency={true} testid="pd-pending" /></div>
        <div onClick={scrollToLedger} className="cursor-pointer"><StatCard label="Payable Pending" value={s.payable_pending} accent="teal" sub="Recognized, unpaid" isCurrency={true} testid="pd-payable" /></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard label="Refunds Received" value={s.refund_received} accent="slate" isCurrency={true} testid="pd-refunds" />
        <StatCard label="Advance Adjusted" value={s.advance_adjusted} accent="slate" isCurrency={true} testid="pd-adjusted" />
        <StatCard label="Net Balance" value={s.net_balance} accent="slate" sub={s.net_balance >= 0 ? "We owe payee" : "Payee owes us"} isCurrency={true} testid="pd-net" />
        <StatCard label="Opening Balance" value={s.opening_balance} accent="slate" isCurrency={true} testid="pd-opening" />
      </div>

      {aging && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
          <AgingCard title="Unpaid Bills · Aging (FIFO)" rows={aging.open_bills} buckets={aging.payable_buckets} color="text-teal-600" testid="pd-aging-bills"
            emptyText="No unpaid bills — nothing to chase." onAction={() => open("pay_agency", { payee_id: id, settle_payable: true, amount: s.payable_pending })} actionLabel="Pay Oldest" />
          <AgingCard title="Unadjusted Advances · Aging (FIFO)" rows={aging.open_advances} buckets={aging.advance_buckets} color="text-violet-600" testid="pd-aging-adv"
            emptyText="No open advances — all allocated." onAction={() => open("adjust_advance", { payee_id: id, amount: s.advance_pending })} actionLabel="Adjust" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
        <PurposeCard title="Expense by Purpose" items={s.expense_by_purpose} color="text-amber-600" />
        <PurposeCard title="Amount Paid by Purpose" items={s.paid_by_purpose} color="text-rose-600" />
      </div>

      <h3 id="pd-ledger" className="font-heading font-semibold text-slate-700 dark:text-slate-200 mb-2">Payee Ledger · Advance {formatINR(ledger.balance)} · Payable {formatINR(ledger.payable || 0)}</h3>
      {ledger.rows.length === 0 ? <EmptyState title="No transactions yet" /> : (
        <TableWrap>
          <thead><tr>
            <Th>Date</Th><Th>Txn</Th><Th>Type</Th><Th>Candidate</Th><Th>Purpose</Th>
            <Th className="text-right">Bill / Expense</Th><Th className="text-right">Paid</Th><Th className="text-right">Adv Adjusted</Th>
            <Th className="text-right">Advance Bal</Th><Th className="text-right">Payable Bal</Th><Th>Proof</Th><Th>By</Th>
          </tr></thead>
          <tbody>
            {ledger.rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <Td>{formatDate(r.txn_date)}</Td><Td className="font-mono">{r.txn_number}</Td>
                <Td>{r.type.replace(/_/g, " ")}{r.overpay_advance > 0 && <div className="text-[10px] text-violet-600">+{formatINR(r.overpay_advance)} to advance</div>}</Td>
                <Td>{r.candidate_name || "-"}</Td>
                <Td>{r.payment_type || "-"}</Td>
                <Td className="text-right font-mono text-amber-600">{r.bill_expense ? formatINR(r.bill_expense) : "-"}</Td>
                <Td className="text-right font-mono text-rose-600">{r.paid ? formatINR(r.paid) : "-"}</Td>
                <Td className="text-right font-mono text-violet-600">{r.adv_adjusted ? formatINR(r.adv_adjusted) : "-"}</Td>
                <Td className="text-right font-mono">{formatINR(r.advance_running)}</Td>
                <Td className="text-right font-mono text-teal-600">{formatINR(r.payable_running || 0)}</Td>
                <Td>{r.attachment_ids?.length ? <AttachmentThumb ids={r.attachment_ids} onClick={() => setViewIds(r.attachment_ids)} testid={`pd-att-${r.id}`} /> : <span className="text-slate-300">-</span>}</Td>
                <Td className="text-slate-400">{r.created_by_name}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
      <AttachmentViewer ids={viewIds || []} open={!!viewIds} onClose={() => setViewIds(null)} />

      <div className="print-area hidden print:block p-6">
        <h1 className="text-lg font-bold uppercase">PRABHAT PROFESSIONAL CONSULTANCY SERVICES PVT. LTD.</h1>
        <p className="text-xs">Main Office - Jamnagar · Branch - Vagheshwari Plot, Porbandar</p>
        <h2 className="text-base font-semibold mt-3">Party Statement — {payee.name} ({payee.type})</h2>
        <p className="text-xs">Total Expense: {formatINR(s.total_expense)} · Total Paid: {formatINR(s.total_paid)} · Advance Pending: {formatINR(s.advance_pending)} · Payable Pending: {formatINR(s.payable_pending)}</p>
        <table className="w-full border-collapse border border-slate-400 text-xs mt-2">
          <thead><tr>{["Date", "Txn", "Type", "Candidate", "Purpose", "Bill/Expense", "Paid", "Adv Adj", "Advance Bal", "Payable Bal", "Proof"].map((h) => <th key={h} className="border border-slate-400 px-2 py-1 text-left">{h}</th>)}</tr></thead>
          <tbody>{ledger.rows.map((r) => (
            <tr key={r.id}>
              <td className="border border-slate-400 px-2 py-1">{formatDate(r.txn_date)}</td>
              <td className="border border-slate-400 px-2 py-1">{r.txn_number}</td>
              <td className="border border-slate-400 px-2 py-1">{r.type.replace(/_/g, " ")}</td>
              <td className="border border-slate-400 px-2 py-1">{r.candidate_name || ""}</td>
              <td className="border border-slate-400 px-2 py-1">{r.payment_type || ""}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{r.bill_expense ? formatINR(r.bill_expense) : ""}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{r.paid ? formatINR(r.paid) : ""}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{r.adv_adjusted ? formatINR(r.adv_adjusted) : ""}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{formatINR(r.advance_running)}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{formatINR(r.payable_running || 0)}</td>
              <td className="border border-slate-400 px-2 py-1">{r.attachment_ids?.length ? `${r.attachment_ids.length} file(s)` : ""}</td>
            </tr>))}</tbody>
        </table>
        <p className="mt-3 font-semibold">Advance Pending: {formatINR(ledger.balance)} · Payable Pending: {formatINR(ledger.payable || 0)}</p>
        {aging?.open_bills?.length > 0 && (
          <>
            <h3 className="text-sm font-semibold mt-4">Unpaid Bills (oldest first)</h3>
            <table className="w-full border-collapse border border-slate-400 text-xs mt-1">
              <thead><tr>{["Date", "Txn", "Bill No", "Open Amount", "Age (days)"].map((h) => <th key={h} className="border border-slate-400 px-2 py-1 text-left">{h}</th>)}</tr></thead>
              <tbody>{aging.open_bills.map((b) => (
                <tr key={b.id}><td className="border border-slate-400 px-2 py-1">{formatDate(b.txn_date)}</td><td className="border border-slate-400 px-2 py-1">{b.txn_number}</td><td className="border border-slate-400 px-2 py-1">{b.bill_number}</td><td className="border border-slate-400 px-2 py-1 text-right">{formatINR(b.open)}</td><td className="border border-slate-400 px-2 py-1">{b.age_days}</td></tr>
              ))}</tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
